import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../../infrastructure/api/default";
import MehrsanOperationsApi from "../../../../../infrastructure/api/mehrsanOperations";
import { getApiOrigin } from "../../../../../infrastructure/utils/resolveUploadUrl";
import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import {
  getEntityId,
  parseApiErrorMessage,
} from "../../../../components/request/case-tabs/mehrsanOperationsUtils";
import { MEHRSAN_PROCESS_TYPE_POWER_PURCHASE } from "../../../../components/request/case-tabs/mehrsanOperationsConstants";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import { parseAllRequestsDetail } from "../all-requests/allRequestsPageUtils";
import {
  buildMehrsanStageSteps,
  buildStepSavePayload,
  countCompletedSteps,
  getNextActionableStep,
  mapUiStatusToMehrsanApiStatus,
  resolveProcessOverallStatus,
} from "../../shared/projectWorkspace/loanMehrsanStage/loanMehrsanStageAdapter";
import {
  MEHRSAN_STAGE_STEPS,
  STEP_STATUS,
} from "../../shared/projectWorkspace/loanMehrsanStage/loanMehrsanStageConstants";
import LoanMehrsanProcessStepsTable from "../../shared/projectWorkspace/loanMehrsanStage/LoanMehrsanProcessStepsTable";
import LoanMehrsanStepEditDialog from "../../shared/projectWorkspace/loanMehrsanStage/LoanMehrsanStepEditDialog";
import MehrsunSendRegistrationCard from "../../shared/projectWorkspace/loanMehrsanStage/MehrsunSendRegistrationCard";

const OPERATIONS_SOURCE = "SolarentOperator";
const DETAIL_API = "SLN/SolarentOperator/AllRequests";
const MEHRSAN_REGISTRATION_STEP_ID = 1;

const normalizeList = (res) => {
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

const findPowerPurchaseProcess = (processes) =>
  processes.find(
    (p) =>
      Number(p.processType ?? p.ProcessType) === MEHRSAN_PROCESS_TYPE_POWER_PURCHASE
  ) || null;

const InfoField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "—"}
    </Typography>
  </Grid>
);

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const MehrsanWorkQueueCaseDrawer = ({ open, queueRow, onClose, onSaved, autoOpenNext = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const requestInfoId = queueRow?.requestInfoId;

  const [loading, setLoading] = useState(false);
  const [requestDetail, setRequestDetail] = useState(null);
  const [fileBaseUrl, setFileBaseUrl] = useState("");
  const [mehrsanProcesses, setMehrsanProcesses] = useState([]);
  const [editState, setEditState] = useState({ open: false, step: null });
  const [saving, setSaving] = useState(false);
  const [shouldAutoOpenNext, setShouldAutoOpenNext] = useState(false);
  const [mehrsunStartToken, setMehrsunStartToken] = useState(0);
  const [highlightMehrsunSend, setHighlightMehrsunSend] = useState(false);

  const loadData = useCallback(async () => {
    if (!requestInfoId) return;
    setLoading(true);
    try {
      const [detailRes, mehrsanRes] = await Promise.all([
        DefaultApiService.getOneApi(DETAIL_API, requestInfoId).catch(() => null),
        MehrsanOperationsApi.getByRequestId(OPERATIONS_SOURCE, requestInfoId),
      ]);

      if (detailRes?.isSuccess !== false) {
        const parsed = parseAllRequestsDetail(detailRes);
        setRequestDetail(parsed.request);
        setFileBaseUrl(parsed.baseUrl || getApiOrigin());
      } else {
        setRequestDetail(null);
        setFileBaseUrl(getApiOrigin());
      }

      const mehrsanList =
        mehrsanRes?.isSuccess === false ? [] : normalizeList(mehrsanRes);
      const mehrsanWithLogs = await Promise.all(
        mehrsanList.map(async (item) => {
          const id = getEntityId(item);
          if (!id) return item;
          try {
            const detail = await MehrsanOperationsApi.getById(OPERATIONS_SOURCE, id);
            return detail?.data ?? detail?.Data ?? item;
          } catch {
            return item;
          }
        })
      );
      setMehrsanProcesses(mehrsanWithLogs);
    } catch {
      setRequestDetail(null);
      setMehrsanProcesses([]);
      setFileBaseUrl(getApiOrigin());
      toast.error("بارگذاری جزئیات پرونده مهرسان ناموفق بود.");
    } finally {
      setLoading(false);
    }
  }, [requestInfoId]);

  useEffect(() => {
    if (!open || !requestInfoId) return;
    setEditState({ open: false, step: null });
    setShouldAutoOpenNext(Boolean(autoOpenNext));
    loadData();
  }, [open, requestInfoId, loadData, autoOpenNext]);

  const mehrsanSteps = useMemo(
    () => buildMehrsanStageSteps(mehrsanProcesses),
    [mehrsanProcesses]
  );
  const mehrsanCompleted = countCompletedSteps(mehrsanSteps);
  const mehrsanOverall = resolveProcessOverallStatus(
    mehrsanCompleted,
    MEHRSAN_STAGE_STEPS.length
  );
  const nextStep = useMemo(
    () => getNextActionableStep(mehrsanSteps),
    [mehrsanSteps]
  );

  useEffect(() => {
    if (!open || loading || !shouldAutoOpenNext) return;
    if (!nextStep) {
      setShouldAutoOpenNext(false);
      return;
    }
    if (Number(nextStep.id) === MEHRSAN_REGISTRATION_STEP_ID) {
      setHighlightMehrsunSend(true);
      setMehrsunStartToken((t) => t + 1);
      setShouldAutoOpenNext(false);
      return;
    }
    setEditState({ open: true, step: nextStep });
    setShouldAutoOpenNext(false);
  }, [open, loading, shouldAutoOpenNext, nextStep]);

  const openRegistrationFlow = useCallback(() => {
    setHighlightMehrsunSend(true);
    setMehrsunStartToken((t) => t + 1);
    toast.info(
      "برای ثبت‌نام اولیه، شرکت توزیع را انتخاب و «ارسال به مهرسان» را بزنید؛ سپس کپچا را وارد کنید."
    );
  }, []);

  const openAdvanceForStep = useCallback(
    (step) => {
      if (!step) {
        toast.info("همه مراحل مهرسان تکمیل شده است.");
        return;
      }
      if (Number(step.id) === MEHRSAN_REGISTRATION_STEP_ID) {
        openRegistrationFlow();
        return;
      }
      setEditState({ open: true, step });
    },
    [openRegistrationFlow]
  );

  const ensureMehrsanProcess = async () => {
    const existing = findPowerPurchaseProcess(mehrsanProcesses);
    if (existing) return getEntityId(existing);

    const res = await MehrsanOperationsApi.create(OPERATIONS_SOURCE, {
      RequestInfoId: Number(requestInfoId),
      ProcessType: MEHRSAN_PROCESS_TYPE_POWER_PURCHASE,
      Title: "قرارداد خرید برق مهرسان",
    });
    if (res?.isSuccess === false) {
      throw new Error(parseApiErrorMessage(res, "ایجاد فرآیند مهرسان ناموفق بود."));
    }
    return res?.data?.id ?? res?.data?.Id ?? res?.Data?.id ?? res?.Data?.Id ?? null;
  };

  const handleSaveStep = async (form) => {
    if (!editState.step) return;
    if (
      Number(editState.step.id) === MEHRSAN_REGISTRATION_STEP_ID &&
      form.status === STEP_STATUS.COMPLETED
    ) {
      setEditState({ open: false, step: null });
      openRegistrationFlow();
      return;
    }
    setSaving(true);
    try {
      const processId = await ensureMehrsanProcess();
      if (!processId) throw new Error("فرآیند مهرسان یافت نشد.");

      const payload = buildStepSavePayload({
        domain: "mehrsan",
        stepId: editState.step.id,
        ...form,
      });
      const res = await MehrsanOperationsApi.addLog(OPERATIONS_SOURCE, {
        MehrsanProcessId: processId,
        Status: mapUiStatusToMehrsanApiStatus(form.status),
        ...payload,
      });
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "ذخیره مرحله مهرسان ناموفق بود."));
      }

      const completedThisStep = form.status === STEP_STATUS.COMPLETED;
      toast.success(
        completedThisStep ? "مرحله تأیید شد." : "وضعیت مرحله ذخیره شد."
      );
      setEditState({ open: false, step: null });
      await loadData();
      onSaved?.();

      // بعد از تأیید، دیالوگ مرحله بعدی را باز کن (جریان شبیه ایریا)
      if (completedThisStep) {
        setShouldAutoOpenNext(true);
      }
    } catch (err) {
      toast.error(err?.message || "ذخیره مرحله ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const detail = requestDetail || {};
  const titleName =
    queueRow?.applicantFullName ||
    detail.fullName ||
    `${detail.firstName || ""} ${detail.lastName || ""}`.trim() ||
    "—";
  const trackingCode =
    queueRow?.requestTrackingCode ||
    detail.referenceID ||
    detail.ReferenceID ||
    requestInfoId;
  const capacityKw =
    queueRow?.plantCapacityKw ??
    detail.plantCapacity ??
    detail.PlantCapacity ??
    null;
  const province =
    detail.provinceTitle ||
    detail.ProvinceTitle ||
    detail.provinceName ||
    detail.ProvinceName ||
    "";
  const city =
    detail.cityTitle || detail.CityTitle || detail.cityName || detail.CityName || "";
  const address = detail.address || detail.Address || "";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={ModalTransition}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        scroll="paper"
        sx={{ zIndex: theme.zIndex.modal }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            minHeight: isMobile ? "100dvh" : "min(560px, 85vh)",
            maxHeight: isMobile ? "100dvh" : "92vh",
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap fontSize="1.05rem">
                پیگیری مهرسان — {titleName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                کد پیگیری {trackingCode || "—"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {nextStep ? (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<SkipNextIcon />}
                  onClick={() => openAdvanceForStep(nextStep)}
                  disabled={loading}
                >
                  مرحله بعد
                </Button>
              ) : null}
              <IconButton
                aria-label="بروزرسانی"
                onClick={loadData}
                size="small"
                disabled={loading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton aria-label="بستن" onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <DialogContent
          dividers
          sx={{
            pt: 1.5,
            pb: 2,
            flex: 1,
            minHeight: isMobile ? 280 : 360,
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : !queueRow ? (
            <Alert severity="warning">اطلاعات پرونده یافت نشد.</Alert>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  اطلاعات پروژه
                </Typography>
                <Grid container spacing={2}>
                  <InfoField label="کد پیگیری" value={trackingCode} />
                  <InfoField label="متقاضی" value={titleName} />
                  <InfoField
                    label="موبایل"
                    value={
                      queueRow.applicantMobile ||
                      detail.mobile ||
                      detail.Mobile ||
                      ""
                    }
                  />
                  <InfoField
                    label="ظرفیت"
                    value={capacityKw ? `${capacityKw} kW` : "—"}
                  />
                  <InfoField
                    label="مسئول پیگیری مهرسان"
                    value={queueRow.assignedToFullName || "بدون مسئول"}
                  />
                  <InfoField
                    label="مرحله فعلی"
                    value={
                      queueRow.queueTab === "completed"
                        ? "تکمیل شده"
                        : queueRow.currentStepTitle || "—"
                    }
                  />
                  <InfoField
                    label="پیشرفت مراحل"
                    value={`${queueRow.completedStepCount ?? mehrsanCompleted}/${queueRow.totalStepCount ?? MEHRSAN_STAGE_STEPS.length}`}
                  />
                  <InfoField
                    label="وضعیت کلی مهرسان"
                    value={mehrsanOverall.label}
                  />
                  <InfoField
                    label="آخرین اقدام"
                    value={
                      queueRow.lastActionAt
                        ? toJalaliFullDate(queueRow.lastActionAt)
                        : "—"
                    }
                  />
                  {queueRow.queueTab !== "completed" ? (
                    <InfoField
                      label="روزهای باز"
                      value={`${queueRow.openDays ?? 0} روز`}
                    />
                  ) : null}
                  {province || city ? (
                    <InfoField
                      label="موقعیت"
                      value={[province, city].filter(Boolean).join(" / ")}
                    />
                  ) : null}
                  {address ? (
                    <Grid item xs={12}>
                      <InfoField label="آدرس" value={address} />
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>

              <Alert severity="warning" sx={{ py: 0.75 }}>
                مرحله ۱ (ثبت‌نام اولیه) فقط از طریق کارت «ارسال به مهرسان» انجام
                می‌شود: انتخاب شرکت توزیع + وارد کردن کپچا. دکمه «مرحله بعد» برای
                مراحل ۲ تا ۴ است.
              </Alert>

              <MehrsunSendRegistrationCard
                requestInfoId={requestInfoId}
                operationsSource={OPERATIONS_SOURCE}
                canEdit
                autoStartToken={mehrsunStartToken}
                highlight={highlightMehrsunSend}
                initialDistributionCompanyId={
                  requestDetail?.distributionCompanyId ??
                  requestDetail?.DistributionCompanyId ??
                  queueRow?.distributionCompanyId ??
                  null
                }
                onSuccess={async () => {
                  setHighlightMehrsunSend(false);
                  await loadData();
                  onSaved?.();
                  setShouldAutoOpenNext(true);
                }}
              />

              <LoanMehrsanProcessStepsTable
                domain="mehrsan"
                title="مراحل فرآیند مهرسان"
                steps={mehrsanSteps}
                loading={loading}
                canEditSteps
                footerNote="مرحله ۱ فقط با ارسال به مهرسان (کپچا). مرحله ۴: بارگذاری قرارداد در دیالوگ ویرایش."
                onEditStep={(step) => {
                  if (Number(step?.id) === MEHRSAN_REGISTRATION_STEP_ID) {
                    openRegistrationFlow();
                    return;
                  }
                  setEditState({ open: true, step });
                }}
                onAdvanceStep={openAdvanceForStep}
              />
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <LoanMehrsanStepEditDialog
        open={editState.open}
        onClose={() => setEditState({ open: false, step: null })}
        step={editState.step}
        domain="mehrsan"
        fileBaseUrl={fileBaseUrl}
        saving={saving}
        quickActionMode
        onSave={handleSaveStep}
      />
    </>
  );
};

MehrsanWorkQueueCaseDrawer.propTypes = {
  open: PropTypes.bool,
  queueRow: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  autoOpenNext: PropTypes.bool,
};

export default MehrsanWorkQueueCaseDrawer;
